// Sales workspace data layer — typed wrappers over the existing backend
// endpoints (no backend change; shapes mirror mrr.service / analytics.service).
import { apiRequest } from '@/lib/supabase';

export interface MrrRow {
  license_id: string;
  contract_id: string;
  customer_type: string | null;
  is_trial: boolean;
  status: string;
  total_mrr: number;
  overage_mrr: number;
  total_peak_overage: number;
  customer_name: string | null;
  license_key_id: string | null;
  scope: string | null;
  vendor_pool: number | null;
  vendor_usage: number;
  org_pool: number | null;
  org_usage: number;
  ciic_pool: number;
  ciic_usage: number;
  overage_allowance_pct: number;
  billing_state: string;
  trial_status: string | null;
  trial_expires_at: string | null;
  trial_days_left: number | null;
}

export interface ByCustomerType {
  customer_type: string;
  license_count: number;
  mrr: number;
}

export interface MrrAnalytics {
  total_mrr: number;
  active_licenses: number;
  trials_active: number;
  conversion_rate: number; // 0..1
  overage_revenue: number;
  by_customer_type: ByCustomerType[];
  rows: MrrRow[];
}

export interface DashboardStats {
  totalLicenses: number;
  activeLicenses: number;
  expiredLicenses: number;
  revokedLicenses: number;
  suspendedLicenses: number;
  trialLicenses: number;
  totalOrganizations: number;
  activeOrganizations: number;
  churnAtRisk: number;
  validationsToday: number;
  monthlyRevenue: number;
}

export type ChurnLevel = 'low' | 'medium' | 'high' | 'critical';
export type UpsellLevel = 'none' | 'low' | 'medium' | 'high';

// license_analytics row joined to its license + org (see analytics.routes.ts).
export interface LicenseAnalyticsRow {
  license_id: string;
  churn_probability: number | null;
  churn_risk_level: ChurnLevel | null;
  upsell_score: number | null;
  upsell_opportunity: UpsellLevel | null;
  license?: {
    license_id?: string;
    tier?: string;
    organization?: { name?: string } | null;
  } | null;
}

// Minimal license row used to enrich analytics with expiry/org (matches /licenses list).
export interface LicenseListRow {
  id: string;
  license_id?: string;
  tier?: string;
  status?: string;
  expires_at?: string | null;
  organization_id?: string | null;
  organization?: { id?: string; name?: string } | null;
  org_name?: string | null;
}

export const getMrrAnalytics = () => apiRequest<MrrAnalytics>('/analytics/mrr');
export const getDashboardStats = () => apiRequest<DashboardStats>('/analytics/dashboard');
export const getChurn = () => apiRequest<LicenseAnalyticsRow[]>('/analytics/churn');
export const getUpsell = () => apiRequest<LicenseAnalyticsRow[]>('/analytics/upsell');
export const getLicenses = () =>
  apiRequest<{ data?: LicenseListRow[] }>('/licenses?pageSize=200').then((r) => r.data || []);
